import { ReactQueryStaleTime } from '@openx/constants/react-query';
import type { RetargetingSegmentOption } from '@openx/types/targeting/retargeting';
import type { UseRetargetingSegmentDetailsFetch } from '@openx/types/targeting/targetingHookTypes';
import { QueryKeys } from '@openx/utils/api/queryKeys';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { RetargetingSegmentByIdDocument } from '../../../graphql/query/segments/GetRetargetingSegmentById.generated';
import { getGraphQLRequestFromClient } from '../../../modules/graphql/types';

export const useRetargetingSegmentDetailsFetch: UseRetargetingSegmentDetailsFetch = ({
  accountUid,
  segmentId,
  enabled = true,
}) => {
  const shouldFetch = enabled && Boolean(segmentId);

  const { data, isLoading, refetch } = useQuery({
    enabled: shouldFetch,
    queryFn: ({ client }) =>
      getGraphQLRequestFromClient(client)(RetargetingSegmentByIdDocument, { id: segmentId ?? '' }),
    queryKey: [QueryKeys.RETARGETING_SEGMENT_BY_ID, segmentId],
    staleTime: ReactQueryStaleTime.MINUTES_15,
  });

  const segment = useMemo<RetargetingSegmentOption | null>(() => {
    const routed = data?.routedSegmentById;

    if (routed?.__typename !== 'RetargetingSegment') {
      return null;
    }

    if (accountUid && routed.account_uid !== accountUid) {
      return null;
    }

    return {
      id: routed.id,
      name: routed.name ?? routed.id,
      disposition: routed.disposition,
    };
  }, [accountUid, data?.routedSegmentById]);

  return {
    isLoading: shouldFetch && isLoading,
    segment,
    refetch,
  };
};
